import React, { useState, useEffect, useMemo } from 'react';
import { API_BASE_URL } from '../config';
import { useSearchParams } from 'react-router-dom';
import { safeParseDate } from '../utils/dateUtils';
import GICampaignSelectionHeader from '../components/GI/GICampaignSelectionHeader';
import GIFormFilters from '../components/GI/GIFormFilters';
import GIPromotersAndPointsTables from '../components/GI/GIPromotersAndPointsTables';
import GIFormResponses from '../components/GI/GIFormResponses';
import GIFormMapper from '../components/GI/GIFormMapper';
import GIAnswersHistogram from '../components/GI/GIAnswersHistogram';
import SortableTable from '../components/SortableTable';
import useAnalytics from '../hooks/useAnalytics';

function GICampaignViewer({ fetchWithAuth, currentUser }) {
  const [searchParams, setSearchParams] = useSearchParams();

  useAnalytics({ currentUser, module: 'GI_CAMPAIGN_VIEWER' });

  // State pour les données et sélections
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [formData, setFormData] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  const [mappedFormIds, setMappedFormIds] = useState(new Set());

  // State pour les filtres (si la campagne est sélectionnée)
  const [answerRangeStartDate, setAnswerRangeStartDate] = useState(null);
  const [answerRangeEndDate, setAnswerRangeEndDate] = useState(null);
  const [initialAnswerRangeStartDate, setInitialAnswerRangeStartDate] = useState(null);
  const [initialAnswerRangeEndDate, setInitialAnswerRangeEndDate] = useState(null);
  const [selectedDates, setSelectedDates] = useState([]);
  const [selectedPromoters, setSelectedPromoters] = useState([]);
  const [selectedInterventionPoints, setSelectedInterventionPoints] = useState([]);
  const [selectedShopSigns, setSelectedShopSigns] = useState([]);

  // State pour la modal des formulaires non remplis
  const [isUnansweredSurveysModalOpen, setIsUnansweredSurveysModalOpen] = useState(false);

  // State pour la modal de mapping DWH
  const [isMapperModalOpen, setIsMapperModalOpen] = useState(false);

  const { track } = useAnalytics({ currentUser, project: selectedCampaign?.data?.CampaignName });

  // Initialiser les dates depuis l'URL ou utiliser les valeurs par défaut
  useEffect(() => {
    const urlStartDate = searchParams.get('startDate');
    const urlEndDate = searchParams.get('endDate');

    if (urlStartDate && urlEndDate) {
      setStartDate(safeParseDate(urlStartDate));
      setEndDate(safeParseDate(urlEndDate));
    } else {
      const now = new Date();
      const defaultStartDate = new Date(now.getFullYear(), now.getMonth(), 1);
      const defaultEndDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      setStartDate(defaultStartDate);
      setEndDate(defaultEndDate);

      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('startDate', defaultStartDate.toISOString().split('T')[0]);
      newSearchParams.set('endDate', defaultEndDate.toISOString().split('T')[0]);
      setSearchParams(newSearchParams);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handler pour les changements de plage de dates
  const handleDateRangeChange = ({ startDate: newStartDate, endDate: newEndDate }) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    setSelectedCampaign(null);
    setFormData(null);

    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('startDate', newStartDate.toISOString().split('T')[0]);
    newSearchParams.set('endDate', newEndDate.toISOString().split('T')[0]);
    newSearchParams.delete('campaignId');
    setSearchParams(newSearchParams);
  };

  // Récupération des formulaires mappés au chargement
  useEffect(() => {
    const fetchMappedForms = async () => {
      try {
        const response = await fetchWithAuth(`${API_BASE_URL}/api/dwh/gi/dwh-mapped-forms`);
        if (response.ok) {
          const data = await response.json();
          setMappedFormIds(new Set(data.formIds));
        } else {
          console.error("Failed to fetch mapped forms");
        }
      } catch (error) {
        console.error("Error fetching mapped forms:", error);
      }
    };

    fetchMappedForms();
  }, [fetchWithAuth]);

  // Récupération des campagnes quand les dates changent
  useEffect(() => {
    const fetchCampaigns = async () => {
      if (!startDate || !endDate) {
        setCampaigns([]);
        setSelectedCampaign(null);
        setFormData(null);
        setFormError(null);
        setLoading(false);
        return;
      }

      try {
        setCampaignsLoading(true);
        const formattedStartDate = startDate.toISOString().split('T')[0];
        const formattedEndDate = endDate.toISOString().split('T')[0];

        const response = await fetchWithAuth(
          `${API_BASE_URL}/api/dwh/gi/campaigns?startDate=${formattedStartDate}&endDate=${formattedEndDate}`
        );

        if (response.ok) {
          const data = await response.json();
          setCampaigns(data.data || []);

          // Vérifier si l'ID de campagne est dans l'URL
          const campaignId = searchParams.get('campaignId');
          if (campaignId && data.data.length > 0) {
            const campaignFromUrl = data.data.find(c => c.CampaignId.toString() === campaignId);
            if (campaignFromUrl) {
              setSelectedCampaign({
                value: campaignFromUrl.CampaignId,
                formId: campaignFromUrl.SurveyId,
                data: campaignFromUrl
              });
            } else {
              // Si la campagne dans l'URL n'existe pas, la supprimer de l'URL
              const newSearchParams = new URLSearchParams(searchParams);
              newSearchParams.delete('campaignId');
              newSearchParams.delete('formId');
              setSearchParams(newSearchParams);
            }
          }
        } else {
          console.error("Failed to fetch campaigns");
          setCampaigns([]);
        }
      } catch (error) {
        console.error("Error fetching campaigns:", error);
        setCampaigns([]);
      } finally {
        setCampaignsLoading(false);
        setLoading(false);
      }
    };

    fetchCampaigns();
  }, [startDate, endDate, fetchWithAuth, searchParams, setSearchParams]);

  // Handler pour sélectionner une campagne depuis le tableau
  const handleCampaignClick = (campaign) => {
    const campaignData = {
      formId: campaign.SurveyId,
      value: campaign.CampaignId,
      data: campaign
    };

    setSelectedCampaign(campaignData);
    setFormData(null);

    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('campaignId', campaign.CampaignId);
    newSearchParams.set('formId', campaign.SurveyId);
    setSearchParams(newSearchParams);

    track('gi_campaign_viewer_view', {
      gi_view_campaign: campaign.CampaignName
    });
  };

  // Handler pour revenir à la liste des campagnes
  const handleBackToCampaigns = () => {
    setSelectedCampaign(null);
    setFormData(null);
    setFormError(null);

    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('campaignId');
    newSearchParams.delete('formId');
    setSearchParams(newSearchParams);
  };

  // Récupération des données de formulaire quand une campagne est sélectionnée
  useEffect(() => {
    const fetchFormData = async () => {
      if (!selectedCampaign) return;

      try {
        setFormLoading(true);
        setFormError(null);
        const response = await fetchWithAuth(
          `${API_BASE_URL}/api/dwh/gi/campaigns/${selectedCampaign.value}/form/${selectedCampaign.formId}`
        );

        if (response.ok) {
          const data = await response.json();
          setFormData(data);
        } else if (response.status === 404) {
          setFormData(null);
          setFormError("no_form");
          console.info("No form data found for this campaign");
        } else {
          setFormData(null);
          setFormError("fetch_error");
          console.error("Failed to fetch form data:", response.status);
        }
      } catch (error) {
        setFormData(null);
        setFormError("fetch_error");
        console.error("Error fetching form data:", error);
      } finally {
        setFormLoading(false);
      }
    };

    fetchFormData();
  }, [selectedCampaign, fetchWithAuth]);

  // Réinitialiser les filtres quand on change de campagne
  useEffect(() => {
    setAnswerRangeStartDate(null);
    setAnswerRangeEndDate(null);
    setInitialAnswerRangeStartDate(null);
    setInitialAnswerRangeEndDate(null);
    setSelectedDates([]);
    setSelectedPromoters([]);
    setSelectedInterventionPoints([]);
    setSelectedShopSigns([]);
  }, [selectedCampaign]);

  // Initialize answer date range from the actual answers (per campaign/form)
  useEffect(() => {
    if (!formData?.data?.answers || formData.data.answers.length === 0) return;

    const parsed = formData.data.answers
      .map(a => safeParseDate(a.AnswerDate))
      .filter(Boolean)
      .sort((a, b) => a.getTime() - b.getTime());

    if (parsed.length === 0) return;

    const minDate = parsed[0];
    const maxDate = parsed[parsed.length - 1];
    setInitialAnswerRangeStartDate(minDate);
    setInitialAnswerRangeEndDate(maxDate);
    setAnswerRangeStartDate(minDate);
    setAnswerRangeEndDate(maxDate);
  }, [formData]);

  // Calculer les options pour les filtres et les données filtrées (uniquement si une campagne est sélectionnée)
  const { dateOptions, promoterOptions, interventionPointOptions, filteredAnswers, filteredFormData, shopSignOptions, histogramAnswers } = useMemo(() => {
    if (!formData || !formData.data.answers || !selectedCampaign) {
      return {
        dateOptions: [],
        promoterOptions: [],
        shopSignOptions: [],
        interventionPointOptions: [],
        filteredAnswers: [],
        filteredFormData: null,
        histogramAnswers: []
      };
    }

    // Fonction pour appliquer un filtre spécifique aux réponses
    const applyFilters = (answers, filters) => {
      let filtered = answers;

      // Filter by answer date range (inclusive)
      if (filters.answerRangeStartDate && filters.answerRangeEndDate) {
        const start = new Date(filters.answerRangeStartDate);
        start.setHours(0, 0, 0, 0);
        const end = new Date(filters.answerRangeEndDate);
        end.setHours(23, 59, 59, 999);

        filtered = filtered.filter(answer => {
          const parsedDate = safeParseDate(answer.AnswerDate);
          if (!parsedDate) return false;
          return parsedDate >= start && parsedDate <= end;
        });
      }

      // Filtre par date
      if (filters.dates && filters.dates.length > 0) {
        const selectedDateValues = filters.dates.map(d => d.value);
        filtered = filtered.filter(answer => {
          const parsedDate = safeParseDate(answer.AnswerDate);
          if (!parsedDate) return false;
          const answerDate = parsedDate.toISOString().split('T')[0];
          return selectedDateValues.includes(answerDate);
        });
      }

      // Filtre par promoteur
      if (filters.promoters && filters.promoters.length > 0) {
        const selectedPromoterValues = filters.promoters.map(p => p.value);
        filtered = filtered.filter(answer => {
          let promoterName;
          if (answer.PromoterName) {
            promoterName = answer.PromoterName;
          } else {
            promoterName = 'Promoteur inconnu';
          }
          return selectedPromoterValues.includes(promoterName);
        });
      }

      // Filtre par point d'intervention
      if (filters.interventionPoints && filters.interventionPoints.length > 0) {
        const selectedInterventionPointValues = filters.interventionPoints.map(ip => ip.value);
        filtered = filtered.filter(answer => {
          return selectedInterventionPointValues.includes(answer.InterventionPointName);
        });
      }

      // Filtre par enseigne
      if (filters.shopSigns && filters.shopSigns.length > 0) {
        const selectedShopSignValues = filters.shopSigns.map(ss => ss.value);
        filtered = filtered.filter(answer => {
          if (!answer.InterventionPointName) return false;
          // Vérifier si une des enseignes sélectionnées est présente dans le nom du point d'intervention
          return selectedShopSignValues.some(shopSign =>
            answer.InterventionPointName.toLowerCase().includes(shopSign.toLowerCase())
          );
        });
      }

      return filtered;
    };

    // Calculer les options pour les dates
    // For the histogram: apply all filters except the day selector, so it remains useful for choosing days.
    const histogramAnswers = applyFilters(formData.data.answers, {
      answerRangeStartDate,
      answerRangeEndDate,
      promoters: selectedPromoters,
      interventionPoints: selectedInterventionPoints,
      shopSigns: selectedShopSigns
    });

    const answersForDates = applyFilters(formData.data.answers, {
      answerRangeStartDate,
      answerRangeEndDate,
      promoters: selectedPromoters,
      interventionPoints: selectedInterventionPoints
    });
    const uniqueDates = [...new Set(answersForDates
      .map(answer => {
        const date = safeParseDate(answer.AnswerDate);
        return date ? date.toISOString().split('T')[0] : null;
      })
      .filter(date => date !== null)
    )].sort();
    const dateOptions = uniqueDates.map(date => ({
      value: date,
      label: new Date(date).toLocaleDateString('fr-FR', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }));

    // Calculer les options pour les promoteurs
    const answersForPromoters = applyFilters(formData.data.answers, {
      answerRangeStartDate,
      answerRangeEndDate,
      dates: selectedDates,
      interventionPoints: selectedInterventionPoints
    });

    const uniquePromoters = [...new Set(answersForPromoters.map(answer => {
      if (answer.PromoterName) {
        return answer.PromoterName;
      }
      return 'Promoteur inconnu';
    }))].sort();

    const promoterOptions = uniquePromoters.map(promoter => ({
      value: promoter,
      label: promoter
    }));

    // Calculer les options pour les points d'intervention
    const answersForInterventionPoints = applyFilters(formData.data.answers, {
      answerRangeStartDate,
      answerRangeEndDate,
      dates: selectedDates,
      promoters: selectedPromoters
    });
    const uniqueInterventionPoints = [...new Set(
      answersForInterventionPoints
        .map(answer => answer.InterventionPointName)
        .filter(name => name)
    )].sort();
    const interventionPointOptions = uniqueInterventionPoints.map(point => ({
      value: point,
      label: point
    }));

    // Calculer les options pour les enseignes à partir des points d'intervention dans les réponses
    const answersForShopSigns = applyFilters(formData.data.answers, {
      answerRangeStartDate,
      answerRangeEndDate,
      dates: selectedDates,
      promoters: selectedPromoters,
      interventionPoints: selectedInterventionPoints
    });
    const uniqueShopSigns = [...new Set(
      answersForShopSigns
        .map(answer => {
          if (!answer.InterventionPointName) return null;
          // Extraire l'enseigne du nom du point d'intervention (partie avant le premier tiret)
          const shopSign = (answer.InterventionPointName.split('-')[0] || '').trim();
          return shopSign || null;
        })
        .filter(shopSign => shopSign)
    )].sort();
    const shopSignOptions = uniqueShopSigns.map(sign => ({
      value: sign,
      label: sign
    }));

    // Appliquer tous les filtres pour obtenir les réponses finales
    const filteredAnswers = applyFilters(formData.data.answers, {
      answerRangeStartDate,
      answerRangeEndDate,
      dates: selectedDates,
      promoters: selectedPromoters,
      interventionPoints: selectedInterventionPoints,
      shopSigns: selectedShopSigns
    });

    // Calculer les promoteurs et points d'intervention selon les filtres
    let filteredPromoters, filteredInterventionPointsData, filteredShopSignsData;

    // Si aucun filtre n'est activé, utiliser les données originales de l'API
    if (
      !answerRangeStartDate &&
      !answerRangeEndDate &&
      selectedDates.length === 0 &&
      selectedPromoters.length === 0 &&
      selectedInterventionPoints.length === 0 &&
      selectedShopSigns.length === 0
    ) {
      filteredPromoters = formData.data.promoters;
      filteredInterventionPointsData = formData.data.interventionPoints || [];
      filteredShopSignsData = formData.data.shopSigns || [];
    } else {
      // Si au moins un filtre est activé, recalculer à partir des réponses filtrées
      const uniqueFilteredPromoterNames = [...new Set(filteredAnswers.map(answer => {
        if (answer.PromoterName) {
          return answer.PromoterName;
        }
        return 'Promoteur inconnu';
      }))];

      filteredPromoters = uniqueFilteredPromoterNames.map(promoterName => ({
        PromoterName: promoterName,
      }));

      const uniqueFilteredInterventionPointNames = [...new Set(
        filteredAnswers
          .map(answer => answer.InterventionPointName)
          .filter(name => name)
      )];
      filteredInterventionPointsData = uniqueFilteredInterventionPointNames.map(pointName => ({
        Name: pointName
      }));

      const uniqueFilteredShopSigns = [...new Set(
        filteredAnswers
          .map(answer => {
            if (!answer.InterventionPointName) return null;
            // Extraire l'enseigne du nom du point d'intervention (partie avant le premier tiret)
            const shopSign = (answer.InterventionPointName.split('-')[0] || '').trim();
            return shopSign || null;
          })
          .filter(shopSign => shopSign)
      )];
      filteredShopSignsData = uniqueFilteredShopSigns.map(shopSign => ({
        Name: shopSign
      }));
    }

    const filteredFormData = {
      ...formData,
      data: {
        ...formData.data,
        answers: filteredAnswers,
        promoters: filteredPromoters,
        interventionPoints: filteredInterventionPointsData,
        shopSigns: filteredShopSignsData
      },
      metadata: {
        ...formData.metadata,
        answers_count: filteredAnswers.length,
        promoters_count: filteredPromoters.length,
        intervention_points_count: filteredInterventionPointsData.length,
        shop_signs_count: filteredShopSignsData.length
      }
    };

    return {
      dateOptions,
      promoterOptions,
      interventionPointOptions,
      filteredAnswers,
      filteredFormData,
      shopSignOptions,
      histogramAnswers
    };
  }, [formData, selectedDates, selectedPromoters, selectedInterventionPoints, selectedCampaign, selectedShopSigns, answerRangeStartDate, answerRangeEndDate]);

  // Handlers pour les filtres
  const handleDateFilterChange = (selectedOptions) => {
    setSelectedDates(selectedOptions || []);
  };

  const handlePromoterFilterChange = (selectedOptions) => {
    setSelectedPromoters(selectedOptions || []);
  };

  const handleInterventionPointFilterChange = (selectedOptions) => {
    setSelectedInterventionPoints(selectedOptions || []);
  };

  const handleShopSignFilterChange = (selectedOptions) => {
    setSelectedShopSigns(selectedOptions || []);
  };

  const handleAnswerRangeChange = ({ startDate: newStartDate, endDate: newEndDate }) => {
    setAnswerRangeStartDate(newStartDate || null);
    setAnswerRangeEndDate(newEndDate || null);
    // Day selection may become invalid after changing the range
    setSelectedDates([]);
  };

  const resetAllFiltersToInitial = () => {
    if (initialAnswerRangeStartDate && initialAnswerRangeEndDate) {
      setAnswerRangeStartDate(initialAnswerRangeStartDate);
      setAnswerRangeEndDate(initialAnswerRangeEndDate);
    }
    setSelectedDates([]);
    setSelectedPromoters([]);
    setSelectedInterventionPoints([]);
    setSelectedShopSigns([]);
  };

  const isFiltersDirty = useMemo(() => {
    const toYmd = (d) => (d ? new Date(d).toISOString().slice(0, 10) : null);
    const currentStart = toYmd(answerRangeStartDate);
    const currentEnd = toYmd(answerRangeEndDate);
    const initialStart = toYmd(initialAnswerRangeStartDate);
    const initialEnd = toYmd(initialAnswerRangeEndDate);

    const rangeDirty =
      (initialStart && currentStart && currentStart !== initialStart) ||
      (initialEnd && currentEnd && currentEnd !== initialEnd);

    return (
      rangeDirty ||
      selectedDates.length > 0 ||
      selectedPromoters.length > 0 ||
      selectedInterventionPoints.length > 0 ||
      selectedShopSigns.length > 0
    );
  }, [
    answerRangeStartDate,
    answerRangeEndDate,
    initialAnswerRangeStartDate,
    initialAnswerRangeEndDate,
    selectedDates,
    selectedPromoters,
    selectedInterventionPoints,
    selectedShopSigns
  ]);

  // Fonction pour rafraîchir les formulaires mappés (appelée après modification du mapping)
  const refreshMappedForms = async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/dwh/gi/dwh-mapped-forms`);
      if (response.ok) {
        const data = await response.json();
        setMappedFormIds(new Set(data.formIds));
      }
    } catch (error) {
      console.error("Error refreshing mapped forms:", error);
    }
  };

  // Définir les colonnes pour le tableau des campagnes
  const campaignColumns = [
    {
      key: 'SurveyId',
      label: 'ID',
      render: (campaign) => campaign.SurveyId
    },
    {
      key: 'SurveyName',
      label: 'Formulaire',
      render: (campaign) => campaign.SurveyName
    },
    {
      key: 'CampaignName',
      label: 'Campagne',
      render: (campaign) => (
        <div className="flex items-center gap-2">
          <div>
            <div className="font-medium text-gray-900 dark:text-gray-100">
              {campaign.CampaignName} (#{campaign.CampaignId})
            </div>
          </div>
          {(campaign.CampaignDescription || campaign.CampaignDisplayName) && (
            <div className="relative group">
              <div className="w-4 h-4 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs cursor-help">
                i
              </div>
              <div className="absolute left-1/2 transform -translate-x-1/2 bottom-full mb-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-normal max-w-sm z-10 break-words">
                {campaign.CampaignDisplayName && (
                  <div>
                    <strong>{campaign.CampaignDisplayName}</strong>
                  </div>
                )}
                {campaign.CampaignDescription && (
                  <div>
                    {campaign.CampaignDescription}
                  </div>
                )}
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
              </div>
            </div>
          )}
        </div>
      )
    },
    {
      key: 'ClientName',
      label: 'Client',
      render: (campaign) => `${campaign.ClientName} (#${campaign.ClientId})`
    },
    {
      key: 'BrandName',
      label: 'Marque',
      render: (campaign) => `${campaign.BrandName} (#${campaign.BrandId})`
    },
    {
      key: 'CompanyName',
      label: 'Entreprise',
      render: (campaign) => campaign.CompanyName
    },
    {
      key: 'CampaignStartDate',
      label: 'Début',
      render: (campaign) => new Date(campaign.CampaignStartDate).toLocaleDateString('fr-FR'),
      rawValue: (campaign) => campaign.CampaignStartDate
    },
    {
      key: 'CampaignEndDate',
      label: 'Fin',
      render: (campaign) => new Date(campaign.CampaignEndDate).toLocaleDateString('fr-FR'),
      rawValue: (campaign) => campaign.CampaignEndDate
    },
    {
      key: 'duration',
      label: 'Durée',
      render: (campaign) => {
        const duration = Math.ceil((new Date(campaign.CampaignEndDate) - new Date(campaign.CampaignStartDate)) / (1000 * 60 * 60 * 24));
        return `${duration} jour${duration > 1 ? 's' : ''}`;
      },
      rawValue: (campaign) => Math.ceil((new Date(campaign.CampaignEndDate) - new Date(campaign.CampaignStartDate)) / (1000 * 60 * 60 * 24))
    },
    {
      key: 'dwh_mapped',
      label: 'DWH Mappé',
      render: (campaign) => {
        const isMapped = mappedFormIds.has(campaign.SurveyId);
        return (
          <div className="flex items-center">
            {isMapped ? (
              <div className="flex items-center text-green-600 dark:text-green-400">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-xs font-medium">Oui</span>
              </div>
            ) : (
              <div className="flex items-center text-gray-400 dark:text-gray-500">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                <span className="text-xs font-medium">Non</span>
              </div>
            )}
          </div>
        );
      },
      rawValue: (campaign) => mappedFormIds.has(campaign.SurveyId) ? 1 : 0
    }
  ];

  return (
    <div className="w-full px-4 py-4 relative">
      {/* Header avec la sélection de dates - uniquement sur la vue tableau */}
      {!selectedCampaign && (
        <GICampaignSelectionHeader
          startDate={startDate}
          endDate={endDate}
          onDatesChange={handleDateRangeChange}
        />
      )}

      {/* Indicateur de chargement */}
      {loading && (
        <div className="flex justify-center my-8">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      )}

      {/* Vue tableau des campagnes */}
      {!selectedCampaign && !loading && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-800 dark:text-white">
              Campagnes avec réponses disponibles
            </h2>
            <div className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 py-1 px-3 rounded-full text-sm">
              {campaigns.length} campagne{campaigns.length > 1 ? 's' : ''}
            </div>
          </div>

          {campaignsLoading ? (
            <div className="flex justify-center my-8">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-green-500"></div>
            </div>
          ) : campaigns.length > 0 ? (
            <div className="h-96">
              <SortableTable
                data={campaigns}
                columns={campaignColumns}
                emptyMessage="Aucune campagne trouvée"
                exportable={true}
                exportFilename="campagnes.csv"
                onRowClick={handleCampaignClick}
                initialSortBy={{ key: 'CampaignStartDate', direction: 'descending' }}
                globalSearch={true}
              />
            </div>
          ) : (
            <div className="text-center py-10 text-gray-500 dark:text-gray-400">
              <p>Aucune campagne trouvée pour cette période.</p>
            </div>
          )}
        </div>
      )}

      {/* Vue détail d'une campagne sélectionnée */}
      {selectedCampaign && !loading && (
        <>
          {/* Header: breadcrumb + back */}
          <div className="mb-6">
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between gap-4">
                <button
                  onClick={handleBackToCampaigns}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                >
                  <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Retour aux campagnes
                </button>

                {(currentUser?.isadmin || currentUser?.issuperadmin) && (
                  <button
                    onClick={() => setIsMapperModalOpen(true)}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors"
                  >
                    <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    Gestion du Mapping DWH
                  </button>
                )}
              </div>

              <nav className="text-sm text-gray-600 dark:text-gray-300">
                <span className="font-medium">{selectedCampaign.data.CompanyName}</span>
                <span className="mx-2 text-gray-400">{'>'}</span>
                <span className="font-medium">{selectedCampaign.data.ClientName}</span>
                <span className="mx-2 text-gray-400">{'>'}</span>
                <span className="font-medium">{selectedCampaign.data.BrandName}</span>
                <span className="mx-2 text-gray-400">{'>'}</span>
                <span className="font-semibold text-gray-900 dark:text-gray-100">
                  {selectedCampaign.data.CampaignDisplayName || selectedCampaign.data.CampaignName}
                </span>
              </nav>
            </div>
          </div>

          {/* Two-column layout */}
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Sidebar */}
            <aside className="w-full lg:w-96 shrink-0 space-y-6 lg:sticky lg:top-4 self-start lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto lg:pr-2">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-5">
                <h2 className="text-lg font-bold text-gray-800 dark:text-white">
                  {selectedCampaign.data.SurveyName} (#{selectedCampaign.data.SurveyId})
                </h2>
                <div className="mt-3 text-sm text-gray-600 dark:text-gray-400">
                  <div className="flex justify-between">
                    <span className="font-medium">Début</span>
                    <span>{new Date(selectedCampaign.data.CampaignStartDate).toLocaleDateString('fr-FR')}</span>
                  </div>
                  <div className="flex justify-between mt-1">
                    <span className="font-medium">Fin</span>
                    <span>{new Date(selectedCampaign.data.CampaignEndDate).toLocaleDateString('fr-FR')}</span>
                  </div>
                  <div className="flex justify-between mt-1">
                    <span className="font-medium">Durée</span>
                    <span>
                      {Math.ceil((new Date(selectedCampaign.data.CampaignEndDate) - new Date(selectedCampaign.data.CampaignStartDate)) / (1000 * 60 * 60 * 24))} jours
                    </span>
                  </div>
                </div>

                {selectedCampaign.data.CampaignDescription && (
                  <div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
                    {selectedCampaign.data.CampaignDescription}
                  </div>
                )}
              </div>

              {/* Counts + filters only once form data is loaded */}
              {formData && !formLoading && filteredFormData && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-5">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                    Indicateurs
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    <div className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 py-1 px-3 rounded-full text-sm">
                      {filteredFormData.metadata.promoters_count} promoteur{filteredFormData.metadata.promoters_count > 1 ? 's' : ''}
                    </div>
                    <div className="bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200 py-1 px-3 rounded-full text-sm">
                      {filteredFormData.metadata.intervention_points_count} point{filteredFormData.metadata.intervention_points_count > 1 ? 's' : ''} d'intervention
                    </div>
                    <div className="bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 py-1 px-3 rounded-full text-sm">
                      {filteredFormData.metadata.answers_count} réponse{filteredFormData.metadata.answers_count > 1 ? 's' : ''}
                    </div>
                    <div className="bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 py-1 px-3 rounded-full text-sm">
                      {filteredFormData.metadata.promoters_count > 0 ? (filteredFormData.metadata.answers_count / filteredFormData.metadata.promoters_count).toFixed(1) : '0'} réponse{filteredFormData.metadata.answers_count > 1 ? 's' : ''} / promoteur
                    </div>
                  </div>
                </div>
              )}

              {formData && !formLoading && (
                <GIFormFilters
                  answerRangeStartDate={answerRangeStartDate}
                  answerRangeEndDate={answerRangeEndDate}
                  initialAnswerRangeStartDate={initialAnswerRangeStartDate}
                  initialAnswerRangeEndDate={initialAnswerRangeEndDate}
                  onAnswerRangeChange={handleAnswerRangeChange}
                  showReset={isFiltersDirty}
                  onResetAll={resetAllFiltersToInitial}
                  dateOptions={dateOptions}
                  promoterOptions={promoterOptions}
                  shopSignOptions={shopSignOptions}
                  interventionPointOptions={interventionPointOptions}
                  selectedDates={selectedDates}
                  selectedPromoters={selectedPromoters}
                  selectedShopSigns={selectedShopSigns}
                  selectedInterventionPoints={selectedInterventionPoints}
                  onDateFilterChange={handleDateFilterChange}
                  onPromoterFilterChange={handlePromoterFilterChange}
                  onShopSignFilterChange={handleShopSignFilterChange}
                  onInterventionPointFilterChange={handleInterventionPointFilterChange}
                  onClearAllFilters={() => {
                    resetAllFiltersToInitial();
                  }}
                />
              )}

              {formData && !formLoading && filteredFormData && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-5">
                  <GIPromotersAndPointsTables
                    variant="sidebar"
                    filteredFormData={filteredFormData}
                    formData={formData}
                    selectedPromoters={selectedPromoters}
                    selectedDates={selectedDates}
                    selectedInterventionPoints={selectedInterventionPoints}
                    selectedShopSigns={selectedShopSigns}
                  />
                </div>
              )}
            </aside>

            {/* Main content */}
            <main className="flex-1 min-w-0">
              {/* Indicateur de chargement pour les données de formulaire */}
              {formLoading && (
                <div className="flex justify-center my-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-green-500"></div>
                </div>
              )}

              {/* Message d'erreur pour l'absence de formulaire */}
              {formError === "no_form" && !formLoading && (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6 mb-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg className="h-6 w-6 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-lg font-medium text-yellow-800 dark:text-yellow-300">
                        Aucun formulaire disponible
                      </h3>
                      <div className="mt-2 text-yellow-700 dark:text-yellow-200">
                        <p>
                          Cette campagne ne contient pas de formulaire ou aucune réponse n'a été enregistrée.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Message d'erreur générale pour les problèmes de chargement */}
              {formError === "fetch_error" && !formLoading && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 mb-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg className="h-6 w-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-lg font-medium text-red-800 dark:text-red-300">
                        Erreur de chargement
                      </h3>
                      <div className="mt-2 text-red-700 dark:text-red-200">
                        <p>
                          Une erreur est survenue lors du chargement des données du formulaire. Veuillez réessayer plus tard.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Main sections */}
              {formData && !formLoading && (
                <>
                  <GIAnswersHistogram
                    answers={histogramAnswers}
                    startDate={answerRangeStartDate}
                    endDate={answerRangeEndDate}
                    title="Réponses"
                  />

                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                    <GIFormResponses
                      formData={formData}
                      filteredFormData={filteredFormData}
                      selectedCampaign={selectedCampaign}
                      fetchWithAuth={fetchWithAuth}
                      currentUser={currentUser}
                    />
                  </div>
                </>
              )}
            </main>
          </div>
        </>
      )}

      {/* Modal de mapping DWH */}
      {selectedCampaign && formData && isMapperModalOpen && (
        <GIFormMapper
          selectedCampaign={selectedCampaign}
          formData={formData}
          fetchWithAuth={fetchWithAuth}
          isOpen={true}
          onClose={() => setIsMapperModalOpen(false)}
          onMappingChange={refreshMappedForms}
        />
      )}
    </div>
  );
}

export default GICampaignViewer;
